import { useEffect, useState } from 'react';
import { IoIosArrowBack } from 'react-icons/io';
import { FiX } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { Pagination } from 'swiper/modules';
import {Swiper,SwiperSlide} from 'swiper/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from "react-helmet-async";
import api from '../../api/api';
import CourseCard from '../../components/Course/CourseCard';
import { useProfileStore } from '../../store/useUserStore';
import { useTranslation } from 'react-i18next';
import { extractYouTubeId, isColorDark } from '../../helpers/helper';
import { FiShare2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
    FacebookShareButton,
    TwitterShareButton,
    WhatsappShareButton,
    EmailShareButton,
    FacebookIcon,
    TwitterIcon,
    WhatsappIcon,
    EmailIcon,
} from 'react-share';


const ExploreCourseView = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { courseBranchId } = useParams()
    const { loading, setLoading } = useProfileStore()
    const [course, setCourse] = useState<any>({
        images: [],
        name: "",
        description: "",
        course_type: 1,
        price: 0,
        branches: [],
        puchased_date: []
    })
    const [previewImage, setPreviewImage] = useState<string | null>(null)
    const [openTimeslotModal, setOpenTimeslotModal] = useState(false)
    const [currentImageIndex, setCurrentImageIndex] = useState<number>(0)
    const [showFullDescription, setShowFullDescription] = useState<boolean>(false)
    const [showShareMenu, setShowShareMenu] = useState(false)

    const navigateBack = () => {
        if (window.history.length > 1) {
            navigate(-1) // Go back to previous page
        } else {
            navigate('/?tab=explore', { replace: true })
        }
    }

    useEffect(() => {
        if (courseBranchId) {
            fetchCourseDetails()
        }
    }, [courseBranchId])

    const fetchCourseDetails = async () => {
        try {
            setLoading(true)
            const response = await api.get(`/parent/explore/details?type=course&course_branch_id=${courseBranchId}`)
            if (response.data && response.data.data) {
                setCourse(response.data.data)
            }
        } catch (error) {
            console.error('Error fetching course details:', error)
        } finally {
            setLoading(false)
        }
    }

    // Generate SEO metadata
    const generateSEOData = () => {
        const courseName = course.course_name || 'Course'
        const city = course.branch_location?.city || course.branch_name || 'Malaysia'
        
        const title = `${courseName} at ${city} | MYKOKU Parent`
        const description = `Discover ${courseName} classes in ${city}. Join thousands of students learning with MYKOKU - Malaysia's trusted platform for Academy Management System.`
        const imageUrl = course.images && course.images.length > 0 
            ? course.images[0] 
            : `${window.location.origin}/assets/img/products/product1.png`
        
        return { title, description, imageUrl }
    }

    const seoData = generateSEOData()

    const shareUrl = typeof window !== 'undefined' ? window.location.href : ''
    const shareTitle = seoData?.title || course.course_name || 'Course'


    return (
        <>
        <Helmet>
            <title>{seoData.title}</title>
            <meta name="description" content={seoData.description} />
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={window.location.href} />
            <meta property="og:title" content={seoData.title} />
            <meta property="og:description" content={seoData.description} />
            <meta property="og:image" content={seoData.imageUrl} />
            
            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={window.location.href} />
            <meta property="twitter:title" content={seoData.title} />
            <meta property="twitter:description" content={seoData.description} />
            <meta property="twitter:image" content={seoData.imageUrl} />
            
            {/* WhatsApp specific */}
            <meta property="og:site_name" content="MYKOKU" />
        </Helmet>
            
        <div className="pb-32 px-2">
            <div className='mb-3 grid grid-cols-6 mx-1'>
                <div className='self-center'><IoIosArrowBack size={24} color='#000' onClick={()=>navigateBack()}/></div>
                <div className='text-center col-span-4 text-lg font-medium truncate'>{course.course_name}</div>
                <div></div>
            </div>
            <div className="">
                <Swiper
                    pagination={{
                        dynamicBullets: true,
                    }}
                    modules={[Pagination]}
                    className="h-96">
                    {course.images && course.images.length > 0 ? (
                        course.images.map((img: string, index: number) => (
                        <SwiperSlide key={index}>
                            <div className="flex justify-center items-center w-full bg-white h-[calc(100%-30px)]">
                                <img 
                                    src={img} 
                                    alt={`Slide ${index}`} 
                                    className="object-cover h-full cursor-pointer"
                                    onClick={() => {
                                        setPreviewImage(img)
                                        setCurrentImageIndex(index)
                                    }}
                                />
                            </div>
                        </SwiperSlide>
                        ))
                    ) : (
                        <SwiperSlide>
                            <div className="flex justify-center items-center w-full bg-white h-[calc(100%-30px)]">
                                <img 
                                    src="/assets/img/products/product1.png" 
                                    alt="Default course image" 
                                    className="object-cover h-full cursor-pointer"
                                    onClick={() => {
                                        setPreviewImage("/assets/img/products/product1.png")
                                        setCurrentImageIndex(0)
                                    }}
                                />
                            </div>
                        </SwiperSlide>
                    )}
                </Swiper>
            </div>

            <div className="pt-4">
                <div className="mb-4">
                    <div className="mb-4">
                        <div className="flex items-start justify-between mb-2 relative">
                            <div className="text-lg font-semibold text-gray-900 mr-4 truncate">
                                {course.course_name}
                            </div>
                            <button
                                onClick={() => setShowShareMenu(prev => !prev)}
                                aria-label="Share course"
                                className="ml-2 p-2 rounded-full bg-theme  text-gray-600"
                            >
                                <FiShare2 size={18} color="#FFF"/>
                            </button>

                            {showShareMenu && (
                                <div className="absolute right-0 top-full mt-2 bg-white rounded-md shadow-lg p-2 z-50 flex items-center gap-2">
                                    <FacebookShareButton url={shareUrl}>
                                        <FacebookIcon size={36} round />
                                    </FacebookShareButton>
                                    <TwitterShareButton url={shareUrl} title={shareTitle}>
                                        <TwitterIcon size={36} round />
                                    </TwitterShareButton>
                                    <WhatsappShareButton url={shareUrl} title={shareTitle}>
                                        <WhatsappIcon size={36} round />
                                    </WhatsappShareButton>
                                    <EmailShareButton url={shareUrl} subject={shareTitle} body={shareUrl}>
                                        <EmailIcon size={36} round />
                                    </EmailShareButton>
                                </div>
                            )}
                        </div>
                        
                        {/* Location */}
                        <div className="flex items-center text-gray-600 mb-3">
                            <span className="text-pink-500 mr-2">📍</span>
                            <span className="text-sm">
                                {course.branch_location ? 
                                    `${course.branch_location.city}, ${course.branch_location.state}.` : 
                                    course.branch_name || 'Location not specified'
                                }
                            </span>
                        </div>
                        
                        {/* Course Type Badge */}
                        {course.course_type && (
                            <div 
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-base"
                                style={{ 
                                    backgroundColor: course.course_type.color ? `#${course.course_type.color}` : '#FED7AA',
                                    color: course.course_type.color && isColorDark(course.course_type.color) ? 'white' : 'black'
                                }}
                            >
                                {course.course_type.icon ? (
                                    <img 
                                        src={course.course_type.icon} 
                                        alt={course.course_type.name} 
                                        className="w-4 h-4 mr-3"
                                        style={{
                                            filter: course.course_type.color && isColorDark(course.course_type.color) ? 'brightness(0) invert(1)' : 'none'
                                        }}
                                    />
                                ) : (
                                    <span className="mr-1">📚</span>
                                )}
                                {course.course_type.name || t("explore.course")}
                            </div>
                        )}
                    </div>
                </div>

                {/* Top separator */}
                <div className="border-t border-gray-200 mb-6"></div>

                {/* Description Section */}
                {course.description && (
                    <div className="mb-6">
                        {/* Description content. When collapsed, limit height and show a gradient to indicate more content. */}
                        {(() => {
                            const plainText = course.description.replace(/<[^>]*>/g, '')
                            const isLong = plainText.length > 200
                            return (
                                <div className="relative">
                                    <div
                                        className={`text-gray-700 leading-relaxed text-sm ${isLong && !showFullDescription ? 'max-h-28 overflow-hidden' : ''}`}
                                        dangerouslySetInnerHTML={{ __html: course.description }}
                                    />

                                    {/* subtle gradient at bottom when collapsed */}
                                    {isLong && !showFullDescription && (
                                        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 "></div>
                                    )}

                                    {/* View More / View Less toggle */}
                                    {isLong && (
                                        <div className="flex justify-center mt-4">
                                            <hr className="flex-grow mt-2 border-gray-300" />
                                            <button
                                                onClick={() => setShowFullDescription(!showFullDescription)}
                                                className="px-4 text-[#00C39A] text-sm font-medium hover:text-[#00A085]"
                                            >
                                                {showFullDescription ? t('explore.view_less') || t('explore.view_less') : t('explore.view_more')}
                                            </button>
                                            <hr className="flex-grow mt-2 border-gray-300" />
                                        </div>
                                    )}
                                </div>
                            )
                        })()}
                    </div>
                )}

                {/* Contact Information */}
                <div className="mb-6 space-y-2">
                    {/* Link */}
                    {course.website_link && (
                        <div className="flex items-start">
                            <span className="font-medium text-gray-900 w-20 text-sm">{t("explore.link")}:</span>
                            <a 
                                href={course.website_link.startsWith('http') ? course.website_link : `https://${course.website_link}`} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-blue-600 hover:text-blue-800 underline break-all text-sm flex-1"
                            >
                                {course.website_link}
                            </a>
                        </div>
                    )}
                    
                    {/* WhatsApp */}
                    {course.whatapps && (
                        <div className="flex items-start">
                            <span className="font-medium text-gray-900 w-20 text-sm">{t("explore.whatsapp")}:</span>
                            <a 
                                href={`https://api.whatsapp.com/send?phone=${"60" + course.whatapps.replace(/[^\d]/g, '')}&text=${encodeURIComponent(`Hello I click the link from MYKOKU, I am interested in ${course.course_name}`)}`} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-blue-600 hover:text-blue-800 underline text-sm flex-1"
                            >
                                {course.whatapps}
                            </a>
                        </div>
                    )}
                    
                </div>

                {/* Bottom separator */}
                <div className="border-b border-gray-200 mb-6"></div>
            </div>

            {/* Branch Course List Section */}
            {course.branch_course_list && course.branch_course_list.length > 0 && (
                <div className="pb-6">
                    <div className="mb-4">
                        <div className="flex items-center">
                            <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center mr-3 overflow-hidden flex-shrink-0">
                                {course.branch_images ? (
                                    <img 
                                        src={course.branch_images} 
                                        alt={course.branch_name || 'Branch'} 
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-pink-500 text-lg">🏢</span>
                                )}
                            </div>
                            <div className="flex items-center flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/explore/branch/${course.branch_id}`)}>
                                <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors flex-1 truncate">
                                    {course.branch_name || '-'}
                                </h3>
                                <svg 
                                    className="w-5 h-5 text-gray-400 ml-2" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M9 5l7 7-7 7" 
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>
                    
                    {/* Horizontal Scrollable Course Cards */}
                    <div className="overflow-x-auto custom-scrollbar">
                        <div className="flex gap-4 pb-2">
                            {course.branch_course_list.map((branchCourse: any, index: number) => (
                                <div 
                                    key={branchCourse.course_branch_id || index}
                                    className="flex-shrink-0 w-48 bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                                    onClick={() => navigate(`/explore/course/${branchCourse.course_branch_id}`)}
                                >
                                    {/* Course Type Tag - positioned above image */}
                                    {branchCourse.course_type && (
                                        <div className="p-3 pb-0">
                                            <div 
                                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-2"
                                                style={{ 
                                                    backgroundColor: branchCourse.course_type.color ? `#${branchCourse.course_type.color}` : '#FED7AA',
                                                    color: branchCourse.course_type.color && isColorDark(branchCourse.course_type.color) ? 'white' : 'black'
                                                }}
                                            >
                                                {branchCourse.course_type.name}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Course Image */}
                                    <div className="w-full h-32 px-2">
                                        <img
                                            src={branchCourse.images && branchCourse.images.length > 0 
                                                ? branchCourse.images[0] 
                                                : '/assets/img/products/product1.png'
                                            }
                                            alt={branchCourse.course_name}
                                            className="w-full h-full object-cover rounded-lg"
                                        />
                                    </div>
                                    
                                    {/* Course Content */}
                                    <div className="p-3">
                                        {/* Course Name */}
                                        <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2 leading-tight">
                                            {branchCourse.course_name}
                                        </h4>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Recommended Courses Section */}
            {course.recommended_courses && course.recommended_courses.length > 0 && (
                <div className="pb-6">
                    <div className="mb-4">
                        {/* Recommended for you title */}
                        <h3 className="text-base font-medium text-gray-500 text-center">
                            {t("explore.recommended_for_you")}
                        </h3>
                        {/* Header with course type and back button */}
                        <div className="flex items-center justify-between my-4">
                            {/* Course Type Tag */}
                            {course.course_type && (
                                <div 
                                    className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium"
                                    style={{ 
                                        backgroundColor: course.course_type.color ? `#${course.course_type.color}` : '#FED7AA',
                                        color: course.course_type.color && isColorDark(course.course_type.color) ? 'white' : 'black'
                                    }}
                                >
                                    {course.course_type.icon ? (
                                        <img 
                                            src={course.course_type.icon} 
                                            alt={course.course_type.name} 
                                            className="w-5 h-5 mr-2"
                                            style={{
                                                filter: course.course_type.color && isColorDark(course.course_type.color) ? 'brightness(0) invert(1)' : 'none'
                                            }}
                                        />
                                    ) : (
                                        <span className="mr-2">🎨</span>
                                    )}
                                    {course.course_type.name}
                                </div>
                            )}
                            
                            {/* Back to explore button */}
                            <button 
                                onClick={() => navigate('/?tab=explore')}
                                className="flex items-center px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600 hover:bg-gray-200 transition-colors"
                            >
                                <svg 
                                    className="w-4 h-4 mr-2" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M15 19l-7-7 7-7" 
                                    />
                                </svg>
                                {t("explore.back_to_explore")}
                            </button>
                        </div>
                        
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        {course.recommended_courses.map((recommendedCourse: any, index: number) => {
                            // Transform schedules to get day array
                            const dayMap: { [key: number]: string } = {
                                1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun'
                            }
                            const concatDayArray: string[] = Array.from(new Set(
                                recommendedCourse.schedules?.map((schedule: any) => 
                                    dayMap[schedule.day?.value] || schedule.day?.label || ''
                                ).filter((day: string) => day) || []
                            ))
                            
                            return (
                                <CourseCard
                                    explore={false}
                                    key={recommendedCourse.course_branch_id || index}
                                    course_id={recommendedCourse.course_branch_id}
                                    name={recommendedCourse.course_name}
                                    concatDayArray={concatDayArray}
                                    images={recommendedCourse.images}
                                    navigateTo={`/explore/course/${recommendedCourse.course_branch_id}`}
                                    isFull={recommendedCourse.is_full}
                                />
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Action Buttons - Sticky Bottom */}
            <div className="fixed bottom-16 left-0 right-0 z-50 mx-auto max-w-[640px] px-4 bg-white  pt-3 pb-3">
                <div className="flex gap-2">
                    {/* WhatsApp Button */}
                    <button 
                        onClick={() => {
                            const message = encodeURIComponent(`Hello I click the link from MYKOKU, I am interested in ${course.course_name}`)
                            window.open(`https://api.whatsapp.com/send?phone=${"60" + course.whatapps.replace(/[^\d]/g, '')}&text=${message}`, '_blank')
                        }}
                        className="flex-1 flex items-center justify-center px-3 py-2.5 s:px-4 s:py-3 bg-theme hover:bg-gray-500 rounded-full text-white font-semibold transition-colors"
                    >
                        <svg 
                            className="w-4 h-4 mr-1.5" 
                            fill="currentColor" 
                            viewBox="0 0 24 24"
                        >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
                        </svg>
                        {t("explore.whatsapp")}
                    </button>

                    {/* Check Timeslot Button */}
                    <button 
                        onClick={() => {
                            setOpenTimeslotModal(true)
                        }}
                        className="flex-1 flex items-center justify-center px-3 py-2.5 s:px-4 s:py-3 bg-blue-500 hover:bg-blue-600 rounded-full text-white font-semibold transition-colors"
                    >
                        <svg 
                            className="w-4 h-4 mr-1.5" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                        >
                            <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                            />
                        </svg>
                        {t("explore.check_timeslot")}
                    </button>
                </div>
            </div>

            {/* Timeslot Modal */}
            <AnimatePresence>
                {openTimeslotModal && (
                    <div 
                        className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-end" 
                        onClick={() => setOpenTimeslotModal(false)}
                    >
                        <motion.div
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ stiffness: 100 }}
                            className="w-full max-w-[648px] bg-white shadow-2xl rounded-t-3xl px-4 pt-4 pb-20"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="mb-6">
                                {/* Modal Header */}
                                <div className="text-center mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900">{t("explore.available_timeslots")}</h3>
                                    <p className="text-sm text-gray-600 mt-1">{course.course_name}</p>
                                </div>

                                {/* Timeslots List */}
                                {course.time_slots && course.time_slots.length > 0 ? (
                                    <div className="space-y-4">
                                        {course.time_slots.map((slot: any, index: number) => (
                                            <div key={slot.id || index} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h4 className="font-medium text-gray-900">{slot.name}</h4>
                                                </div>
                                                
                                                {/* Schedules */}
                                                {slot.schedules && slot.schedules.length > 0 ? (
                                                    <div className="space-y-2">
                                                        {slot.schedules.map((schedule: any, scheduleIndex: number) => (
                                                            <div key={schedule.course_schedule_id || scheduleIndex} className="bg-gray-50 rounded-md p-3">
                                                                
                                                                {/* Class Timeslots */}
                                                                {schedule.class_timeslots && schedule.class_timeslots.length > 0 ? (
                                                                    <div className="grid grid-cols-1 gap-2">
                                                                        {schedule.class_timeslots.map((timeslot: any, timeslotIndex: number) => {
                                                                            const formatTime = (time: string) => {
                                                                                if (!time) return 'N/A';
                                                                                const [h, m] = time.split(":").map(Number);
                                                                                const date = new Date();
                                                                                date.setHours(h, m);
                                                                                return date.toLocaleTimeString("en-US", {
                                                                                    hour: "2-digit",
                                                                                    minute: "2-digit",
                                                                                    hour12: true,
                                                                                });
                                                                            };

                                                                            return (
                                                                                <div key={timeslotIndex} className="flex items-center justify-between text-sm">
                                                                                    <span className="text-gray-600">
                                                                                        {timeslot.day || 'Day not specified'}
                                                                                    </span>
                                                                                    <span className="font-medium text-gray-900">
                                                                                        {timeslot.start_time && timeslot.end_time 
                                                                                            ? `${formatTime(timeslot.start_time)} - ${formatTime(timeslot.end_time)}`
                                                                                            : t("explore.time_not_specified")
                                                                                        }
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-sm text-gray-500 italic">
                                                                        {t("explore.no_class_timeslots")}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-gray-500 italic">
                                                        {t("explore.no_schedules")}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <div className="text-gray-400 mb-2">
                                            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <p className="text-gray-500">{t("explore.no_timeslots_available")}</p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Preview Image Modal */}
            {previewImage !== null && (
                <div className="fixed inset-0 bg-gray-700 bg-opacity-100 place-content-center z-[1100]" onClick={() => setPreviewImage(null)} >
                    <div className="relative max-w-full max-h-full p-4" onClick={(e) => e.stopPropagation()} style={{
                        maxWidth: "640px",
                        marginLeft: "auto",
                        marginRight: "auto",
                        paddingLeft: "1rem",
                        paddingRight: "1rem",
                    }}>
    
                        {/* Close button */}
                        <button onClick={() => { setPreviewImage(null) }} className="absolute top-2 left-2 bg-white bg-opacity-50 text-black rounded-full p-1.5 shadow-md hover:bg-opacity-100 z-10" aria-label="Close" >
                            <FiX size={18} />
                        </button>

                        <div className="w-full h-[60vh] max-h-96">
                            <Swiper
                                initialSlide={currentImageIndex}
                                pagination={{
                                    dynamicBullets: true,
                                }}
                                modules={[Pagination]}
                                className="h-full"
                                onSlideChange={(swiper) => {
                                    // Only update the index, don't update previewImage to avoid conflicts
                                    setCurrentImageIndex(swiper.realIndex);
                                }}
                            >
                                {course.images && course.images.length > 0 ? (
                                    course.images.map((img: string, index: number) => (
                                        <SwiperSlide key={index}>
                                            <div className="flex justify-center items-center h-full">
                                                <img src={img} alt={`Preview ${index}`} className="h-full object-contain rounded-md" />
                                            </div>
                                        </SwiperSlide>
                                    ))
                                ) : (
                                    <SwiperSlide>
                                        <div className="flex justify-center items-center h-full">
                                            <img src="/assets/img/products/product1.png" alt="Default preview" className="h-full object-contain rounded-md" />
                                        </div>
                                    </SwiperSlide>
                                )}
                            </Swiper>
                        </div>
    
                        <button
                            onClick={async () => {
                                try {
                                    const response = await fetch(previewImage, { mode: 'cors' });
                                    const blob = await response.blob();
                                    const blobUrl = window.URL.createObjectURL(blob);
    
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = 'course-image.jpg';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
    
                                    window.URL.revokeObjectURL(blobUrl);
                                } catch (err) {
                                    console.error("Download image failed:", err);
                                }
                        }}
                        className="absolute -bottom-10 right-4 bg-theme text-white px-3 py-1.5 text-sm rounded-full shadow-lg"
                        >
                        {t("explore.download_image")}
                        </button>
                    </div>
                </div>
            )}

        </div>
        </>
    )
}

export default ExploreCourseView